import React, { useState, useEffect } from 'react';
import './App.css';
import AudioPlayer from './components/AudioPlayer';
import EpisodeList from './components/EpisodeList';
import PodcastList from './components/PodcastList';
import AddPodcastForm from './components/AddPodcastForm';
import PodcastDetailModal from './components/PodcastDetailModal';
import { Episode, Podcast } from './types';
import { db, testStorage } from './utils/db';

type ViewMode = 'episodes' | 'podcasts';

function App() {
  const [episodes, setEpisodes] = useState<Episode[]>([]);
  const [podcasts, setPodcasts] = useState<Podcast[]>([]);
  const [currentEpisode, setCurrentEpisode] = useState<Episode | null>(null);
  const [autoPlay, setAutoPlay] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailPodcast, setDetailPodcast] = useState<Podcast | null>(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>('episodes');
  const [selectedPodcast, setSelectedPodcast] = useState<Podcast | null>(null);

  useEffect(() => {
    loadData();
    // Test storage on startup
    testStorage();
  }, []);

  const loadData = async () => {
    try {
      await db.init();
      const [loadedPodcasts, loadedEpisodes] = await Promise.all([
        db.getPodcasts(),
        db.getEpisodes(),
      ]);
      setPodcasts(loadedPodcasts);
      const sortedEpisodes = loadedEpisodes.sort((a, b) =>
        new Date(b.pubDate).getTime() - new Date(a.pubDate).getTime()
      );
      setEpisodes(sortedEpisodes);
      console.log(`Loaded ${loadedPodcasts.length} podcasts and ${loadedEpisodes.length} episodes`);

      // Restore last playing episode (without auto-playing)
      const savedEpisodeId = localStorage.getItem('currentEpisodeId');
      if (savedEpisodeId) {
        const restoredEpisode = sortedEpisodes.find(ep => ep.id === savedEpisodeId);
        if (restoredEpisode) {
          setAutoPlay(false);
          setCurrentEpisode(restoredEpisode);
          console.log('Restored episode:', restoredEpisode.title);
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePlay = (episode: Episode) => {
    setAutoPlay(true);
    setCurrentEpisode(episode);
    localStorage.setItem('currentEpisodeId', episode.id);
    console.log('Now playing:', episode.title);
  };

  const handleDownload = async (episode: Episode) => {
    try {
      const response = await fetch(episode.audioUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${episode.title}.mp3`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      episode.downloaded = true;
      episode.downloadedBlob = blob;
      await db.updateEpisode(episode);
      loadData();
    } catch (error) {
      console.error('Error downloading episode:', error);
      alert('Failed to download episode');
    }
  };

  const handleSelectPodcast = async (podcast: Podcast) => {
    setDetailPodcast(podcast);
    setShowDetailModal(true);
  };

  const handleViewEpisodes = () => {
    if (detailPodcast) {
      setSelectedPodcast(detailPodcast);
      setViewMode('episodes');
      setShowDetailModal(false);
    }
  };

  const handleDeletePodcast = async (podcastId: string) => {
    try {
      await db.deletePodcast(podcastId);
      // Also delete episodes for this podcast
      const allEpisodes = await db.getEpisodes();
      for (const episode of allEpisodes) {
        if (episode.podcastId === podcastId) {
          // Note: We'd need a deleteEpisode method in db.ts
          // For now, we'll just reload the data
        }
      }
      await loadData();
      if (selectedPodcast?.id === podcastId) {
        setSelectedPodcast(null);
      }
    } catch (error) {
      console.error('Error deleting podcast:', error);
      alert('Failed to delete podcast');
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  const displayedEpisodes = selectedPodcast
    ? episodes.filter(ep => ep.podcastId === selectedPodcast.id)
    : episodes;

  return (
    <div className="App">
      <header className="app-header">
        <div className="header-content">
          <h1>🎧 PodTxt</h1>
          <button onClick={() => setShowAddForm(true)} className="add-btn">
            + Add
          </button>
        </div>
      </header>

      <AudioPlayer episode={currentEpisode} autoPlay={autoPlay} />

      <div className="view-tabs">
        <button
          className={`view-tab ${viewMode === 'episodes' ? 'active' : ''}`}
          onClick={() => {
            setViewMode('episodes');
            setSelectedPodcast(null);
          }}
        >
          Episodes {selectedPodcast && `(${selectedPodcast.title})`}
        </button>
        <button
          className={`view-tab ${viewMode === 'podcasts' ? 'active' : ''}`}
          onClick={() => setViewMode('podcasts')}
        >
          Podcasts ({podcasts.length})
        </button>
      </div>

      <main className="app-main">
        {viewMode === 'episodes' ? (
          displayedEpisodes.length === 0 ? (
            <div className="empty-state">
              <p>No episodes yet. Click "+ Add" to subscribe to a podcast!</p>
            </div>
          ) : (
            <EpisodeList
              episodes={displayedEpisodes}
              onPlay={handlePlay}
              onDownload={handleDownload}
            />
          )
        ) : (
          <PodcastList
            podcasts={podcasts}
            onSelect={handleSelectPodcast}
            onDelete={handleDeletePodcast}
          />
        )}
      </main>

      {showAddForm && (
        <AddPodcastForm
          onPodcastAdded={loadData}
          onClose={() => setShowAddForm(false)}
        />
      )}

      {showDetailModal && detailPodcast && (
        <PodcastDetailModal
          podcast={detailPodcast}
          onClose={() => setShowDetailModal(false)}
          onViewEpisodes={handleViewEpisodes}
        />
      )}
    </div>
  );
}

export default App;
